import React, {
  useState, useMemo, useEffect, useCallback,
} from 'react';
import {
  Card, Col, Form, Input, Row, Button, Icon, message,
} from 'antd';
import ImageUploadButton from '../../../ImageUpload/ImageUploadButton';
import { RATIO_SQUARE } from '../../../ImageUpload/ImageUploadRatio';
import { useTranslation } from 'react-i18next';
import { useModuleFields } from '../../../ModuleForm/ModuleForm.hooks';

function MenuOptionsCollapse({
  module,
  form,
  onChange,
  onOptionUpdate, // Callback for updating options in parent
  onOptionDelete, // Callback for deleting options in parent
  // onOptionCreate, // Callback for creating new options in parent (handled by autosave)
}) {
  const { t } = useTranslation();
  const {
    getFieldDecorator,
    getFieldValue,
    setFieldsValue,
  } = form;
  const moduleFields = useModuleFields(getFieldValue('type'));

  // Initialize options from module.options (existing options) or empty array
  const [options, setOptions] = useState([]);
  const [isUpdating, setIsUpdating] = useState(false);
  // Debounced onChange to prevent excessive autosave calls - max once per second
  const [updateTimer, setUpdateTimer] = useState(null);

  const debouncedOnChange = useCallback(() => {
    // Clear existing timer
    if (updateTimer) {
      clearTimeout(updateTimer);
    }

    // Set new timer to trigger update after 1 second
    const newTimer = setTimeout(() => {
      console.log('MenuOptionsCollapse: Triggering onChange for existing options update');
      onChange();
      setUpdateTimer(null);
    }, 1000);

    setUpdateTimer(newTimer);
  }, [onChange, updateTimer]);

  // Load existing options when component mounts or module changes
  // Also sync when module.options changes to pick up newly created options with real IDs
  useEffect(() => {
    if (module && module.options && module.options.length > 0) {
      // Transform existing options to the format expected by the form
      const existingOptions = module.options.map((option) => ({
        id: option.id,
        title: option.title || '',
        subtitle: option.subtitle || '',
        url: option.url || '',
        image: option.image || '',
        weight: option.weight || 0,
        published: option.published !== false,
      }));

      // Merge with local options: match by title+url to update negative IDs with real IDs
      // while preserving any local changes (like images that were just added)
      const mergedOptions = existingOptions.map((existingOption) => {
        // Try to find a matching local option (with negative ID) by title and url
        const matchingLocalOption = options.find(
          (localOpt) => localOpt.id < 0
            && localOpt.title === existingOption.title
            && localOpt.url === existingOption.url,
        );

        // If we found a match, merge the data but use the real ID
        if (matchingLocalOption) {
          return {
            ...existingOption,
            // Preserve image from local if it exists (might have been added after creation)
            // Use local image if it's not empty, otherwise use existing
            image: (matchingLocalOption.image && matchingLocalOption.image.trim() !== '')
              ? matchingLocalOption.image
              : existingOption.image,
            // Preserve other fields that might have been updated locally
            subtitle: matchingLocalOption.subtitle !== undefined ? matchingLocalOption.subtitle : existingOption.subtitle,
            weight: matchingLocalOption.weight !== undefined ? matchingLocalOption.weight : existingOption.weight,
            published: matchingLocalOption.published !== undefined ? matchingLocalOption.published : existingOption.published,
          };
        }
        return existingOption;
      });

      // Add any local options that don't have matches (newly added but not yet created)
      const localOnlyOptions = options.filter((localOpt) => {
        if (localOpt.id > 0) return false; // Already exists in module.options
        // Check if this local option matches any existing option by title+url
        return !existingOptions.some(
          (existingOpt) => existingOpt.title === localOpt.title && existingOpt.url === localOpt.url,
        );
      });

      const finalOptions = [...mergedOptions, ...localOnlyOptions];
      setOptions(finalOptions);
      setFieldsValue({ menu_options: finalOptions });
    } else if (options.length === 0) {
      // If no module options and no local options, clear everything
      setOptions([]);
      setFieldsValue({ menu_options: [] });
    }
  }, [module?.id, module?.options?.length]); // Depend on options length to sync when options are created

  // Cleanup timer on unmount
  useEffect(() => () => {
    if (updateTimer) {
      clearTimeout(updateTimer);
    }
  }, [updateTimer]);

  // Reorder weights to ensure they are sequential (0, 1, 2, 3...)
  const reorderWeights = useCallback(() => {
    const newOptions = options.map((option, index) => ({
      ...option,
      weight: index,
    }));
    setOptions(newOptions);
    setFieldsValue({ menu_options: newOptions });

    // Always trigger onChange for autosave to work - the duplicate prevention in ModuleForm will handle preventing multiple creations
    debouncedOnChange();
  }, [options, setFieldsValue, debouncedOnChange]);

  // Add debugging to track weight assignments
  useEffect(() => {
    if (options.length > 0) {
      const weights = options.map((o) => o.weight).sort((a, b) => a - b);
      console.log('Current option weights:', weights);

      // Check for duplicate weights
      const duplicates = weights.filter((weight, index) => weights.indexOf(weight) !== index);
      if (duplicates.length > 0) {
        console.warn('Duplicate weights detected:', duplicates);
      }

      // Check for gaps in weights
      const gaps = [];
      for (let i = 0; i < weights.length; i++) {
        if (weights[i] !== i) {
          gaps.push(i);
        }
      }
      if (gaps.length > 0) {
        console.warn('Gaps in weights detected:', gaps);
      }
    }
  }, [options]);

  const visible = useMemo(
    () => moduleFields.menu_options,
    [moduleFields],
  );

  if (!visible) return null;

  const addOption = () => {
    // Generate a temporary ID that's negative to avoid conflicts with real database IDs
    // and ensure it fits within 32-bit integer range
    const tempId = -(Date.now() % 1000000); // Use modulo to keep within safe range

    // Calculate the next available weight by finding the highest existing weight and adding 1
    // This prevents duplicate weights and gaps in the sequence
    let nextWeight = 0;
    if (options.length > 0) {
      const existingWeights = options.map((option) => option.weight || 0);
      const maxWeight = Math.max(...existingWeights);
      nextWeight = maxWeight + 1;
      console.log('Adding new option - existing weights:', existingWeights, 'next weight:', nextWeight);
    }

    const newOption = {
      id: tempId,
      title: '',
      subtitle: '',
      url: '',
      image: '',
      weight: nextWeight,
      published: true,
    };
    const newOptions = [...options, newOption];
    setOptions(newOptions);
    setFieldsValue({ menu_options: newOptions });

    console.log(`Added new option with temp ID ${tempId}. Triggering onChange for autosave.`);

    // Trigger onChange to allow autosave to work - the duplicate prevention in ModuleForm will handle preventing multiple creations
    debouncedOnChange();
  };

  const removeOption = async (index) => {
    const optionToRemove = options[index];

    // If it's an existing option (has a real ID), delete it from database
    if (optionToRemove.id && typeof optionToRemove.id === 'number' && optionToRemove.id > 0) {
      if (onOptionDelete) {
        try {
          await onOptionDelete(optionToRemove.id);
        } catch (error) {
          message.error('Failed to delete option');
          return;
        }
      }
    }

    // Always remove from local state and trigger onChange for autosave to work
    const newOptions = options.filter((_, i) => i !== index);
    setOptions(newOptions);
    setFieldsValue({ menu_options: newOptions });
    debouncedOnChange();
  };

  const updateOption = async (index, field, value) => {
    const newOptions = [...options];
    const optionToUpdate = { ...newOptions[index], [field]: value };
    newOptions[index] = optionToUpdate;
    setOptions(newOptions);
    setFieldsValue({ menu_options: newOptions });

    console.log(`Updating option ${index}, field: ${field}, value: ${value}, option ID: ${optionToUpdate.id}`);

    // Determine the real option ID
    let realOptionId = null;

    // If it's an existing option (has a real ID), use it directly
    if (optionToUpdate.id && typeof optionToUpdate.id === 'number' && optionToUpdate.id > 0) {
      realOptionId = optionToUpdate.id;
    } else if (optionToUpdate.id && typeof optionToUpdate.id === 'number' && optionToUpdate.id < 0) {
      // If it's a new option (negative ID), check if it has been created in the database
      // by looking it up in module.options using title and url as identifiers
      if (module && module.options && optionToUpdate.title && optionToUpdate.url) {
        const createdOption = module.options.find(
          (opt) => opt.title === optionToUpdate.title && opt.url === optionToUpdate.url,
        );
        if (createdOption && createdOption.id) {
          realOptionId = createdOption.id;
          console.log(`Found created option with real ID ${realOptionId} for temp ID ${optionToUpdate.id}`);

          // Update local state with the real ID
          optionToUpdate.id = realOptionId;
          newOptions[index] = optionToUpdate;
          setOptions(newOptions);
          setFieldsValue({ menu_options: newOptions });
        }
      }
    }

    // If we have a real option ID, update it in the database
    if (realOptionId && onOptionUpdate) {
      console.log(`Option ${realOptionId} is existing, updating in database`);
      try {
        setIsUpdating(true);
        await onOptionUpdate(realOptionId, { [field]: value });
      } catch (error) {
        message.error('Failed to update option');
      } finally {
        setIsUpdating(false);
      }
    }

    // Always trigger onChange for autosave to work - the duplicate prevention in ModuleForm will handle preventing multiple creations
    console.log(`Triggering onChange for autosave (option ID: ${optionToUpdate.id})`);
    debouncedOnChange();
  };

  const moveOption = async (index, direction) => {
    if (direction === 'up' && index === 0) return;
    if (direction === 'down' && index === options.length - 1) return;

    const newOptions = [...options];
    const targetIndex = direction === 'up' ? index - 1 : index + 1;

    // Swap weights
    const tempWeight = newOptions[index].weight;
    newOptions[index].weight = newOptions[targetIndex].weight;
    newOptions[targetIndex].weight = tempWeight;

    // Swap positions
    [newOptions[index], newOptions[targetIndex]] = [newOptions[targetIndex], newOptions[index]];

    setOptions(newOptions);
    setFieldsValue({ menu_options: newOptions });

    // Update weights in database for existing options only
    const hasExistingOptions = newOptions[index].id > 0 || newOptions[targetIndex].id > 0;

    if (onOptionUpdate && hasExistingOptions) {
      try {
        setIsUpdating(true);
        // Only update existing options in database
        if (newOptions[index].id > 0) {
          await onOptionUpdate(newOptions[index].id, { weight: newOptions[index].weight });
        }
        if (newOptions[targetIndex].id > 0) {
          await onOptionUpdate(newOptions[targetIndex].id, { weight: newOptions[targetIndex].weight });
        }
      } catch (error) {
        message.error('Failed to update option order');
      } finally {
        setIsUpdating(false);
      }
    }

    // Always trigger onChange for autosave to work - the duplicate prevention in ModuleForm will handle preventing multiple creations
    debouncedOnChange();
  };

  return (
    <Card
      title={t('modules.form.menu_options.title', 'Menu Options')}
      style={{ marginBottom: 16 }}
      extra={
        <div>
          <Button
            type="dashed"
            onClick={reorderWeights}
            icon="reload"
            style={{ marginRight: 8 }}
            title="Reorder weights sequentially"
          >
            {t('modules.form.menu_options.reorder_weights', 'Reorder Weights')}
          </Button>
          <Button type="dashed" onClick={addOption} icon="plus">
            {t('modules.form.menu_options.add_option', 'Add Option')}
          </Button>
        </div>
      }
    >
      {isUpdating && (
        <div style={{ textAlign: 'center', padding: '10px', color: '#1890ff' }}>
          <Icon type="loading" /> {t('modules.form.menu_options.updating', 'Updating options...')}
        </div>
      )}

      {/* Weight issue warning */}
      {(() => {
        if (options.length === 0) return null;
        const weights = options.map((o) => o.weight).sort((a, b) => a - b);
        const duplicates = weights.filter((weight, index) => weights.indexOf(weight) !== index);
        const gaps = [];
        for (let i = 0; i < weights.length; i++) {
          if (weights[i] !== i) {
            gaps.push(i);
          }
        }

        if (duplicates.length > 0 || gaps.length > 0) {
          return (
            <div style={{
              textAlign: 'center',
              padding: '10px',
              color: '#faad14',
              backgroundColor: '#fff7e6',
              border: '1px solid #ffd591',
              borderRadius: '4px',
              marginBottom: '16px',
            }}>
              <Icon type="warning" /> Weight issues detected!
              {duplicates.length > 0 && ` Duplicates: ${duplicates.join(', ')}`}
              {gaps.length > 0 && ` Gaps: ${gaps.join(', ')}`}
              <Button
                type="link"
                size="small"
                onClick={reorderWeights}
                style={{ marginLeft: '8px' }}
              >
                Fix Weights
              </Button>
            </div>
          );
        }
        return null;
      })()}

      {options.length === 0 && (
        <div style={{ textAlign: 'center', padding: '20px', color: '#999' }}>
          {t('modules.form.menu_options.no_options', 'No menu options added yet. Click "Add Option" to get started.')}
        </div>
      )}

      {options.map((option, index) => (
        <Card
          key={option.id}
          size="small"
          style={{ marginBottom: 16 }}
          title={
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <span>
                {t('modules.form.menu_options.option', 'Option')} {index + 1}
                <span style={{
                  marginLeft: '8px',
                  fontSize: '12px',
                  color: '#999',
                  backgroundColor: '#f5f5f5',
                  padding: '2px 6px',
                  borderRadius: '3px',
                }}>
                  Weight: {option.weight}
                </span>
              </span>
              <Icon
                type="delete"
                onClick={() => removeOption(index)}
                style={{
                  cursor: 'pointer',
                  fontSize: '16px',
                  color: '#ff4d4f',
                  marginLeft: '8px',
                }}
                title="Delete this option"
              />
            </div>
          }
          extra={
            <div>
              <Button
                type="link"
                size="small"
                icon="arrow-up"
                disabled={index === 0}
                onClick={() => moveOption(index, 'up')}
              />
              <Button
                type="link"
                size="small"
                icon="arrow-down"
                disabled={index === options.length - 1}
                onClick={() => moveOption(index, 'down')}
              />
            </div>
          }
        >
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                label={t('modules.form.menu_options.title_label', 'Title')}
                required
                validateStatus={!option.title ? 'error' : ''}
                help={!option.title ? t('modules.form.menu_options.title_required', 'Title is required') : ''}
              >
                <Input
                  placeholder={t('modules.form.menu_options.title_placeholder', 'Enter option title')}
                  value={option.title}
                  onChange={(e) => updateOption(index, 'title', e.target.value)}
                />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                label={t('modules.form.menu_options.url_label', 'URL')}
                required
                validateStatus={!option.url ? 'error' : ''}
                help={!option.url ? t('modules.form.menu_options.url_required', 'URL is required') : ''}
              >
                <Input
                  placeholder={t('modules.form.menu_options.url_placeholder', 'https://example.com')}
                  value={option.url}
                  onChange={(e) => updateOption(index, 'url', e.target.value)}
                />
              </Form.Item>
            </Col>
          </Row>
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                label={t('modules.form.menu_options.subtitle_label', 'Subtitle')}
              >
                <Input
                  placeholder={t('modules.form.menu_options.subtitle_placeholder', 'Optional subtitle')}
                  value={option.subtitle}
                  onChange={(e) => updateOption(index, 'subtitle', e.target.value)}
                />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                label={t('modules.form.menu_options.image_label', 'Image')}
              >
                <ImageUploadButton
                  value={option.image}
                  ratio={RATIO_SQUARE}
                  onChange={(filename) => updateOption(index, 'image', filename)}
                />
              </Form.Item>
            </Col>
          </Row>
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                label={t('modules.form.menu_options.weight_label', 'Order')}
              >
                <Input
                  type="number"
                  value={option.weight}
                  onChange={(e) => updateOption(index, 'weight', parseInt(e.target.value, 10))}
                  min={0}
                />
              </Form.Item>
            </Col>
          </Row>
        </Card>
      ))}

      {/* Hidden field to store the options data */}
      {getFieldDecorator('menu_options', {
        initialValue: options,
      })(<Input type="hidden" />)}
    </Card>
  );
}

export default MenuOptionsCollapse;
